import { useState, useRef, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import advertisementService from '../../services/advertisementService';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';

const PLACEMENT_OPTIONS = [
  { key: 'home_top', labelEn: 'Home - Top Banner', labelAr: 'الرئيسية - بانر علوي' },
  { key: 'home_middle', labelEn: 'Home - Middle Section', labelAr: 'الرئيسية - القسم الأوسط' },
  { key: 'home_bottom', labelEn: 'Home - Bottom Banner', labelAr: 'الرئيسية - بانر سفلي' },
  { key: 'header', labelEn: 'Header', labelAr: 'الهيدر' },
  { key: 'footer', labelEn: 'Footer', labelAr: 'التذييل' },
  { key: 'product_page_sidebar', labelEn: 'Product Page - Sidebar', labelAr: 'صفحة المنتج - الشريط الجانبي' },
  { key: 'product_page_bottom', labelEn: 'Product Page - Bottom', labelAr: 'صفحة المنتج - أسفل' },
  { key: 'category_page_top', labelEn: 'Category Page - Top', labelAr: 'صفحة التصنيف - أعلى' },
  { key: 'cart_page', labelEn: 'Cart Page', labelAr: 'صفحة السلة' },
  { key: 'checkout_page', labelEn: 'Checkout Page', labelAr: 'صفحة الدفع' },
];

const MOBILE_SCREENS = [
  { value: 'home', labelEn: 'Home', labelAr: 'الرئيسية' },
  { value: 'browse', labelEn: 'Browse', labelAr: 'تصفح العطور' },
  { value: 'detail', labelEn: 'Product Detail', labelAr: 'تفاصيل المنتج' },
];

const MOBILE_SLOTS = {
  home: [
    { value: 'banner_top', labelEn: 'Top Banner (Carousel)', labelAr: 'البانر العلوي (كاروسيل)' },
  ],
  browse: [
    { value: 'inline_grid', labelEn: 'Inline Grid Ad (every 6 items)', labelAr: 'إعلان داخل الشبكة (كل 6 منتجات)' },
  ],
  detail: [
    { value: 'banner_bottom', labelEn: 'Bottom Banner (below description)', labelAr: 'البانر السفلي (أسفل الوصف)' },
  ],
};

const MOBILE_PLATFORMS = [
  { value: 'all', labelEn: 'All Platforms', labelAr: 'جميع المنصات' },
  { value: 'android', labelEn: 'Android Only', labelAr: 'أندرويد فقط' },
  { value: 'ios', labelEn: 'iOS Only', labelAr: 'iOS فقط' },
];

const MOBILE_ACTION_TYPES = [
  { value: 'none', labelEn: 'No Action', labelAr: 'بدون إجراء' },
  { value: 'perfume', labelEn: 'Open Product Page', labelAr: 'فتح صفحة منتج' },
  { value: 'url', labelEn: 'Open External URL', labelAr: 'فتح رابط خارجي' },
];

const emptyMobilePlacement = {
  enabled: false,
  screen: 'home',
  slot: 'banner_top',
  platform: 'all',
  actionType: 'none',
  actionTarget: '',
  startAt: '',
  endAt: '',
};

const emptyForm = {
  titleEn: '',
  titleAr: '',
  descriptionEn: '',
  descriptionAr: '',
  displayOrder: 0,
  link: '',
  showCta: true,
  ctaTextEn: '',
  ctaTextAr: '',
  isActive: true,
  image: null,
  placementKeys: [],
  mobilePlacement: { ...emptyMobilePlacement },
};

const CreateAdvertisementModal = ({ isOpen, onClose, onSuccess, adToEdit }) => {
  const { get, isRtl } = useLocalization();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);
  const isEditMode = Boolean(adToEdit?.id);

  useEffect(() => {
    if (!isOpen) return;
    if (adToEdit) {
      const webPlacements = (adToEdit.placements || []).filter((p) => !p.mobileScreen);
      const mobileP = (adToEdit.placements || []).find((p) => p.mobileScreen);
      const placementKeys = webPlacements.map((p) => p.placementKey);
      const mobilePlacement = mobileP
        ? {
            enabled: true,
            screen: mobileP.mobileScreen || 'home',
            slot: mobileP.mobileSlot || 'banner_top',
            platform: mobileP.mobilePlatform || 'all',
            actionType: mobileP.actionType || 'none',
            actionTarget: mobileP.actionTarget || '',
            startAt: mobileP.startAtUtc ? mobileP.startAtUtc.slice(0, 16) : '',
            endAt: mobileP.endAtUtc ? mobileP.endAtUtc.slice(0, 16) : '',
          }
        : { ...emptyMobilePlacement };
      setFormData({
        titleEn: adToEdit.titleEn ?? '',
        titleAr: adToEdit.titleAr ?? '',
        descriptionEn: adToEdit.descriptionEn ?? '',
        descriptionAr: adToEdit.descriptionAr ?? '',
        displayOrder: Number(adToEdit.displayOrder) ?? 0,
        link: adToEdit.link ?? '',
        showCta: adToEdit.showCta !== false,
        ctaTextEn: adToEdit.ctaTextEn ?? '',
        ctaTextAr: adToEdit.ctaTextAr ?? '',
        isActive: adToEdit.isActive !== false,
        image: null,
        placementKeys,
        mobilePlacement,
      });
      const existingImageUrl = adToEdit.imageUrlFull || adToEdit.imageUrl;
      setImagePreview(existingImageUrl || null);
    } else {
      setFormData(emptyForm);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    setError(null);
  }, [isOpen, adToEdit]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      setError(null);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: null }));
    if (isEditMode && adToEdit) {
      setImagePreview(adToEdit.imageUrlFull || adToEdit.imageUrl || null);
    } else {
      setImagePreview(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const togglePlacement = (key) => {
    setFormData((prev) => ({
      ...prev,
      placementKeys: prev.placementKeys.includes(key)
        ? prev.placementKeys.filter((k) => k !== key)
        : [...prev.placementKeys, key],
    }));
  };

  const setMobile = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      mobilePlacement: {
        ...prev.mobilePlacement,
        [field]: value,
        // Reset slot when screen changes
        ...(field === 'screen' ? { slot: MOBILE_SLOTS[value]?.[0]?.value ?? '' } : {}),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const hasNewImage = formData.image instanceof File;
    const hasExistingImage = isEditMode && (adToEdit?.imageUrl || formData.imageUrl);
    if (!isEditMode && !hasNewImage) {
      setError(isRtl ? 'الصورة مطلوبة' : 'Advertisement image is required');
      return;
    }
    if (isEditMode && !hasNewImage && !adToEdit?.imageUrl) {
      setError(isRtl ? 'الصورة مطلوبة' : 'Advertisement image is required');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        ...formData,
        imageUrl: isEditMode && !hasNewImage ? (adToEdit?.imageUrl ?? formData.imageUrl) : undefined,
        deletePlacementIds: isEditMode && adToEdit?.placements?.length ? adToEdit.placements.map((p) => p.id) : [],
      };
      if (isEditMode) {
        await advertisementService.update(adToEdit.id, payload);
      } else {
        await advertisementService.create(formData);
      }
      setFormData(emptyForm);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message ?? err.message ?? (isRtl ? 'حدث خطأ' : 'Error');
      setError(typeof msg === 'string' ? msg : (isRtl ? 'حدث خطأ' : 'Error'));
    } finally {
      setIsLoading(false);
    }
  };

  const title = isEditMode
    ? (isRtl ? 'تعديل الإعلان' : 'Update Advertisement')
    : (isRtl ? 'إضافة إعلان جديد' : 'Add New Advertisement');
  const subtitle = isEditMode
    ? (isRtl ? 'تعديل بيانات الإعلان والمواضع' : 'Edit advertisement details and placements')
    : (isRtl ? 'إنشاء بانر ترويجي مع مواضع مخصصة' : 'Create promotional banner with custom placements');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="large"
      footer={null}
      className="create-advertisement-modal"
    >
      <p className="modal-create-ad-subtitle">{subtitle}</p>
      <form onSubmit={handleSubmit} className="create-ad-form">
        {/* Basic Information */}
        <section className="form-section">
          <h4 className="form-section-title">{isRtl ? 'المعلومات الأساسية' : 'Basic Information'}</h4>
          <div className="form-row form-row-2">
            <Input
              label={isRtl ? 'العنوان (عربي)' : 'Title (Arabic)'}
              value={formData.titleAr}
              onChange={(e) => setFormData({ ...formData, titleAr: e.target.value })}
              placeholder={isRtl ? 'مثال: تخفيضات الصيف' : 'e.g., Summer Sale'}
            />
            <Input
              label={isRtl ? 'العنوان (إنجليزي)' : 'Title (English)'}
              value={formData.titleEn}
              onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
              placeholder="e.g., Summer Sale 2025"
            />
          </div>
          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label">{isRtl ? 'الوصف (إنجليزي)' : 'Description (English)'}</label>
              <textarea
                className="form-input form-textarea"
                value={formData.descriptionEn}
                onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                placeholder={isRtl ? 'وصف مختصر' : 'Brief description'}
                rows={3}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'الوصف (عربي)' : 'Description (Arabic)'}</label>
              <textarea
                className="form-input form-textarea"
                value={formData.descriptionAr}
                onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                placeholder={isRtl ? 'وصف مختصر' : 'Brief description'}
                rows={3}
              />
            </div>
          </div>
        </section>

        {/* Display Order and Link URL */}
        <section className="form-section">
          <h4 className="form-section-title">{isRtl ? 'ترتيب العرض والرابط' : 'Display Order and Link URL'}</h4>
          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label">{isRtl ? 'ترتيب العرض' : 'Display Order'}</label>
              <input
                type="number"
                className="form-input"
                min={0}
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value, 10) || 0 })}
              />
              <p className="form-hint">{isRtl ? 'الأرقام الأقل تظهر أولاً' : 'Lower numbers appear first.'}</p>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'رابط URL' : 'Link URL'}</label>
              <input
                type="url"
                className="form-input"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://..."
              />
              <p className="form-hint">
                {isRtl ? 'أين ينتقل المستخدم عند النقر على الإعلان (اختياري).' : 'Where users go when clicking the ad (optional).'}
              </p>
            </div>
          </div>
        </section>

        {/* CTA Button */}
        <section className="form-section">
          <h4 className="form-section-title">{isRtl ? 'زر الإجراء (CTA)' : 'CTA Button'}</h4>
          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label form-label-inline">
                <input
                  type="checkbox"
                  checked={formData.showCta}
                  onChange={(e) => setFormData({ ...formData, showCta: e.target.checked })}
                />
                <span>{isRtl ? 'إظهار الزر' : 'Show button'}</span>
              </label>
              <p className="form-hint">
                {isRtl ? 'يمكنك إخفاء الزر مع بقاء الإعلان مرئياً.' : 'You can hide the button while keeping the banner visible.'}
              </p>
            </div>
          </div>
          <div className="form-row form-row-2">
            <Input
              label={isRtl ? 'نص الزر (عربي)' : 'Button Text (Arabic)'}
              value={formData.ctaTextAr}
              onChange={(e) => setFormData({ ...formData, ctaTextAr: e.target.value })}
              placeholder={isRtl ? 'مثال: تسوق الآن' : 'e.g., Shop Now'}
              disabled={!formData.showCta}
            />
            <Input
              label={isRtl ? 'نص الزر (إنجليزي)' : 'Button Text (English)'}
              value={formData.ctaTextEn}
              onChange={(e) => setFormData({ ...formData, ctaTextEn: e.target.value })}
              placeholder="e.g., Shop Now"
              disabled={!formData.showCta}
            />
          </div>
        </section>

        {/* Active */}
        <section className="form-section">
          <div className="form-group">
            <label className="form-label form-label-inline">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span>{isRtl ? 'نشط (مرئي للمستخدمين)' : 'Active (visible to users)'}</span>
            </label>
          </div>
        </section>

        {/* Placements */}
        <section className="form-section">
          <h4 className="form-section-title">{isRtl ? 'المواضع' : 'Placements'}</h4>
          <p className="form-hint" style={{ marginBottom: 'var(--sp-3)' }}>
            {isRtl ? 'اختر أين يظهر هذا الإعلان (اختياري)' : 'Choose where this ad appears (optional)'}
          </p>
          <div className="placement-grid">
            {PLACEMENT_OPTIONS.map(({ key, labelEn, labelAr }) => (
              <label key={key} className="placement-checkbox">
                <input
                  type="checkbox"
                  checked={formData.placementKeys.includes(key)}
                  onChange={() => togglePlacement(key)}
                />
                <span>{isRtl ? labelAr : labelEn}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Mobile App Placement */}
        <section className="form-section">
          <h4 className="form-section-title">
            {isRtl ? '📱 التطبيق المحمول (اختياري)' : '📱 Mobile App Placement (optional)'}
          </h4>
          <div className="form-group">
            <label className="form-label form-label-inline">
              <input
                type="checkbox"
                checked={formData.mobilePlacement.enabled}
                onChange={(e) => setMobile('enabled', e.target.checked)}
              />
              <span>{isRtl ? 'تفعيل هذا الإعلان في التطبيق المحمول' : 'Show this ad in the mobile app'}</span>
            </label>
            <p className="form-hint">
              {isRtl
                ? 'حدد أين يظهر الإعلان في تطبيق رمال الخليج للجوال'
                : 'Choose where this ad appears in the Rimal Al Khalij mobile app'}
            </p>
          </div>

          {formData.mobilePlacement.enabled && (
            <>
              <div className="form-row form-row-2">
                <div className="form-group">
                  <label className="form-label">{isRtl ? 'الشاشة' : 'Screen'}</label>
                  <select
                    className="form-input"
                    value={formData.mobilePlacement.screen}
                    onChange={(e) => setMobile('screen', e.target.value)}
                  >
                    {MOBILE_SCREENS.map((s) => (
                      <option key={s.value} value={s.value}>{isRtl ? s.labelAr : s.labelEn}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{isRtl ? 'موضع الإعلان' : 'Ad Slot'}</label>
                  <select
                    className="form-input"
                    value={formData.mobilePlacement.slot}
                    onChange={(e) => setMobile('slot', e.target.value)}
                  >
                    {(MOBILE_SLOTS[formData.mobilePlacement.screen] || []).map((s) => (
                      <option key={s.value} value={s.value}>{isRtl ? s.labelAr : s.labelEn}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row form-row-2">
                <div className="form-group">
                  <label className="form-label">{isRtl ? 'المنصة' : 'Platform'}</label>
                  <select
                    className="form-input"
                    value={formData.mobilePlacement.platform}
                    onChange={(e) => setMobile('platform', e.target.value)}
                  >
                    {MOBILE_PLATFORMS.map((p) => (
                      <option key={p.value} value={p.value}>{isRtl ? p.labelAr : p.labelEn}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{isRtl ? 'نوع الإجراء عند النقر' : 'Action on Tap'}</label>
                  <select
                    className="form-input"
                    value={formData.mobilePlacement.actionType}
                    onChange={(e) => setMobile('actionType', e.target.value)}
                  >
                    {MOBILE_ACTION_TYPES.map((a) => (
                      <option key={a.value} value={a.value}>{isRtl ? a.labelAr : a.labelEn}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.mobilePlacement.actionType !== 'none' && (
                <div className="form-group">
                  <label className="form-label">
                    {formData.mobilePlacement.actionType === 'perfume'
                      ? (isRtl ? 'معرّف المنتج (Product ID)' : 'Product ID (UUID)')
                      : (isRtl ? 'الرابط الخارجي' : 'External URL')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.mobilePlacement.actionTarget}
                    onChange={(e) => setMobile('actionTarget', e.target.value)}
                    placeholder={
                      formData.mobilePlacement.actionType === 'perfume'
                        ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                        : 'https://...'
                    }
                  />
                  <p className="form-hint">
                    {formData.mobilePlacement.actionType === 'perfume'
                      ? (isRtl ? 'معرّف المنتج الذي ينتقل إليه المستخدم عند النقر' : 'The product UUID the app navigates to on tap')
                      : (isRtl ? 'يُفتح في متصفح داخل التطبيق' : 'Opens in in-app browser')}
                  </p>
                </div>
              )}

              <div className="form-row form-row-2">
                <div className="form-group">
                  <label className="form-label">{isRtl ? 'تاريخ البدء (اختياري)' : 'Start Date (optional)'}</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.mobilePlacement.startAt}
                    onChange={(e) => setMobile('startAt', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{isRtl ? 'تاريخ الانتهاء (اختياري)' : 'End Date (optional)'}</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.mobilePlacement.endAt}
                    onChange={(e) => setMobile('endAt', e.target.value)}
                  />
                  <p className="form-hint">
                    {isRtl ? 'الإعلان يختفي تلقائياً بعد هذا التاريخ' : 'Ad automatically hides after this date'}
                  </p>
                </div>
              </div>
            </>
          )}
        </section>

        {/* Advertisement Image */}
        <section className="form-section">
          <h4 className="form-section-title">{isRtl ? 'صورة الإعلان' : 'Advertisement Image'}</h4>
          <div
            className="image-upload-zone"
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="image-upload-input"
              aria-hidden
            />
            {imagePreview ? (
              <div className="image-upload-preview">
                <img src={imagePreview} alt="Preview" />
                <button
                  type="button"
                  className="image-upload-remove"
                  onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                  aria-label={isRtl ? 'إزالة الصورة' : 'Remove image'}
                >
                  ×
                </button>
              </div>
            ) : (
              <>
                <div className="image-upload-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
                <span className="image-upload-text">{isRtl ? 'انقر لرفع الصورة' : 'Click to upload image'}</span>
              </>
            )}
          </div>
        </section>

        {error && (
          <div className="form-error-message" role="alert">
            {error}
          </div>
        )}

        <div className="modal-create-ad-footer">
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading
              ? (isRtl ? 'جاري الحفظ...' : 'Saving...')
              : isEditMode
                ? (isRtl ? 'حفظ التغييرات' : 'Update Advertisement')
                : (isRtl ? 'إنشاء الإعلان' : 'Create Advertisement')}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            {get('cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateAdvertisementModal;
